"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import type { HeroSlide } from "@/components/home/hero-slides";

/**
 * Auto-crossfading hero background — ports the BlueKeys iOS Discover hero
 * (DiscoverView.swift) to the web: a stack of real Egyptian region photos that
 * fade one into the next, with a readability scrim, a per-slide place caption,
 * and elongated pager dots.
 *
 * LCP-safe: only the FIRST slide is rendered on the server / first paint (it
 * carries `priority`). The remaining slides mount after hydration, so they
 * never compete with the hero image for the initial load. The crossfade
 * rotation runs on every device; only the Ken Burns zoom is disabled under
 * `prefers-reduced-motion` (via CSS) — so a phone with Reduce Motion enabled
 * still advances through the slides instead of freezing on the first one.
 *
 * Photos live in /public/hero (self-hosted, 16:9). Place names are bilingual
 * and chosen by the `locale` prop; the "{n} homes" count comes in per slide,
 * resolved server-side (see components/home/Hero.tsx). 6 s per slide.
 */
// Slide data (photos + the compound names behind each count) lives in a plain
// module so the server <Hero/> can resolve live counts before handing it over.
export default function HeroSlideshow({ locale, slides }: { locale: string; slides: HeroSlide[] }) {
  const t = useTranslations("home");
  const [active, setActive] = useState(0);
  // Whether to mount slides beyond the first (deferred so they don't compete
  // with the LCP hero image on first paint).
  const [extra, setExtra] = useState(false);

  useEffect(() => {
    // Defer mounting the remaining slides to just after first paint (via rAF,
    // so we never call setState synchronously in the effect body) — keeps them
    // off the LCP path — then start the crossfade. Rotation runs on ALL devices;
    // only the Ken Burns zoom respects prefers-reduced-motion (handled in CSS),
    // so phones with "Reduce Motion" enabled still advance instead of freezing
    // on slide 1.
    const raf = requestAnimationFrame(() => setExtra(true));
    const id = setInterval(
      () => setActive((i) => (i + 1) % slides.length),
      6000,
    );
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, [slides.length]);

  const current = slides[active] ?? slides[0];
  if (!current) return null;
  const isAr = locale === "ar";

  return (
    <div
      className="absolute inset-0 -z-10 overflow-hidden pointer-events-none"
      aria-hidden
    >
      <style>{`
        @keyframes bkHeroZoom { from { transform: scale(1); } to { transform: scale(1.08); } }
        .bk-hero-kenburns { animation: bkHeroZoom 20s ease-in-out infinite alternate; will-change: transform; }
        @media (prefers-reduced-motion: reduce) { .bk-hero-kenburns { animation: none; } }
      `}</style>
      {slides.map((s, i) => {
        if (i !== 0 && !extra) return null;
        return (
          <Image
            key={s.src}
            src={s.src}
            alt=""
            fill
            priority={i === 0}
            sizes="100vw"
            style={{ objectPosition: s.pos ?? "center" }}
            className={`bk-hero-kenburns object-cover transition-opacity duration-700 ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          />
        );
      })}

      {/* Veil — top-to-bottom ink wash so the centred headline, the search
          and the trust strip pinned to the bottom all stay legible over a pale
          sky (Alexandria/Cairo) as well as a deep-blue sea. */}
      <div
        className="hidden md:block absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom,rgba(11,18,51,0.35) 0%,rgba(11,18,51,0.25) 40%,rgba(11,18,51,0.78) 100%)",
        }}
      />
      {/* Phone veil: the photo band is short and the headline sits on its
          lower edge, so only the bottom half is darkened and the sky stays
          bright. */}
      <div
        className="md:hidden absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom,rgba(11,18,51,0.18) 0%,rgba(11,18,51,0) 30%,rgba(11,18,51,0.82) 100%)",
        }}
      />

      {/* Phone: place chip (top-start) + pager dots (top-end). */}
      <div className="md:hidden absolute top-3 start-4 max-w-[70%] truncate rounded-full bg-[rgba(11,18,51,0.45)] backdrop-blur-sm px-2.5 py-1 text-[11.5px] font-semibold text-white">
        {isAr ? current.ar : current.en}
        <span className="font-normal text-white/80"> · {isAr ? current.regionAr : current.regionEn}</span>
      </div>
      <div className="md:hidden flex absolute top-[18px] end-4 items-center gap-1">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === active ? "w-3.5 bg-ss-coral opacity-100" : "w-1 bg-white opacity-60"
            }`}
          />
        ))}
      </div>

      {/* Place caption (bottom-start) + pager dots (bottom-end), desktop only. */}
      <div className="hidden lg:flex flex-col gap-0.5 absolute bottom-6 start-6 text-white [text-shadow:0_1px_12px_rgba(11,18,51,.6)]">
        <div className="h-display text-[17px] font-semibold leading-tight">
          {isAr ? current.ar : current.en}
        </div>
        <div className="text-[12.5px] text-white/80">
          {isAr ? current.regionAr : current.regionEn}
          {current.count > 0 && (
            <> · {t("homesCount", { count: current.count, n: current.countLabel })}</>
          )}
        </div>
      </div>
      <div className="hidden lg:flex absolute bottom-7 end-6 items-center gap-1.5">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === active ? "w-4 bg-ss-coral opacity-100" : "w-1.5 bg-white opacity-50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
